/** Phase 10.2 — /app/strategy-memory — Read-only Strategy Memory list + drawer.
 *
 *  Renders validated + proposed learnings from `strategy_memory_entries`
 *  with filters (dimension, subject, status) and a side drawer showing
 *  full claim, structured JSON, confidence, sample_size, evidence agent
 *  runs, and a "View in Knowledge Graph" button that opens the existing
 *  `KnowledgeGraphDrawer` on the entry's `subject_kg_node_id`.
 *
 *  Write actions (archive/supersede) intentionally omitted here —
 *  full curator UI lands in Phase 10.3.
 */
import { useEffect, useMemo, useState } from "react";
import { api, auth } from "../../lib/api";
import { toast } from "sonner";
import { useLocale } from "../../lib/i18n";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  Card, CardContent, CardHeader, CardTitle,
} from "../../components/ui/card";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "../../components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import KnowledgeGraphDrawer from "../../components/app/KnowledgeGraphDrawer";
import SupersedeModal from "../../components/app/SupersedeModal";
import EvidenceKgPaths from "../../components/app/EvidenceKgPaths";
import AutopilotUsageSection from "../../components/app/AutopilotUsageSection";
import StrategyMemoryBadge from "../../components/app/StrategyMemoryBadge";
import { BrainCircuit, ExternalLink, Network, Repeat, Replace } from "lucide-react";

const DIMENSIONS = [
  "audience_insight", "creative_pattern", "channel_effectiveness",
  "regional_pattern", "timing_pattern", "offer_positioning",
  "compliance_learning", "objection_pattern",
];
const STATUSES = ["validated", "proposed", "superseded", "archived"];

const COPY = {
  de: {
    title: "Strategie-Gedächtnis",
    subtitle: "Validierte Lernungen, die Agents in zukünftige Prompts ziehen.",
    filters: "Filter",
    status: "Status",
    dimension: "Dimension",
    subject: "Subjekt (KG-Node-ID)",
    subject_ph: "z. B. campaign-123",
    all: "Alle",
    empty: "Keine Einträge für diese Filter.",
    loading: "Lade…",
    claim: "Aussage",
    claim_structured: "Strukturierte Aussage",
    confidence: "Konfidenz",
    sample_size: "Stichprobe",
    created_by_agent: "Erzeugt von",
    evidence: "Evidenz (Agent-Runs)",
    evidence_kg_paths: "KG-Pfade",
    kg_button: "Im Knowledge Graph anzeigen",
    kg_no_node: "Kein KG-Node verknüpft",
    drawer_title: "Lern-Eintrag",
    drawer_subtitle: "Read-only. Kuratieren in Phase 10.3.",
    no_evidence: "Keine Agent-Runs verknüpft.",
    no_kg_paths: "Keine KG-Pfade erfasst.",
    superseded_by: "Ersetzt durch",
    dim: {
      audience_insight: "Zielgruppen-Einsicht",
      creative_pattern: "Kreativ-Muster",
      channel_effectiveness: "Kanal-Wirksamkeit",
      regional_pattern: "Regional-Muster",
      timing_pattern: "Timing-Muster",
      offer_positioning: "Angebots-Positionierung",
      compliance_learning: "Compliance-Lernung",
      objection_pattern: "Einwand-Muster",
    },
    st: {
      validated: "Validiert", proposed: "Vorgeschlagen",
      superseded: "Ersetzt", archived: "Archiviert",
    },
  },
  en: {
    title: "Strategy Memory",
    subtitle: "Validated learnings that agents pull into future prompts.",
    filters: "Filters",
    status: "Status",
    dimension: "Dimension",
    subject: "Subject (KG node id)",
    subject_ph: "e.g. campaign-123",
    all: "All",
    empty: "No entries match these filters.",
    loading: "Loading…",
    claim: "Claim",
    claim_structured: "Structured claim",
    confidence: "Confidence",
    sample_size: "Sample size",
    created_by_agent: "Created by",
    evidence: "Evidence (agent runs)",
    evidence_kg_paths: "KG paths",
    kg_button: "View in Knowledge Graph",
    kg_no_node: "No KG node linked",
    drawer_title: "Memory entry",
    drawer_subtitle: "Read-only. Curation lands in Phase 10.3.",
    no_evidence: "No agent runs linked.",
    no_kg_paths: "No KG paths recorded.",
    superseded_by: "Superseded by",
    dim: {
      audience_insight: "Audience insight",
      creative_pattern: "Creative pattern",
      channel_effectiveness: "Channel effectiveness",
      regional_pattern: "Regional pattern",
      timing_pattern: "Timing pattern",
      offer_positioning: "Offer positioning",
      compliance_learning: "Compliance learning",
      objection_pattern: "Objection pattern",
    },
    st: {
      validated: "Validated", proposed: "Proposed",
      superseded: "Superseded", archived: "Archived",
    },
  },
};

function StatusBadge({ status, t }) {
  const map = {
    validated: "bg-emerald-100 text-emerald-800 border-emerald-300",
    proposed:  "bg-amber-100 text-amber-800 border-amber-300",
    superseded:"bg-slate-100 text-slate-700 border-slate-300",
    archived:  "bg-zinc-100 text-zinc-600 border-zinc-300",
  };
  return (
    <Badge
      variant="outline"
      className={map[status] || map.archived}
      data-testid={`sm-status-${status}`}
    >
      {t.st[status] || status}
    </Badge>
  );
}

function ConfidenceBar({ value }) {
  const pct = Math.round((value || 0) * 100);
  return (
    <div className="flex items-center gap-2 min-w-[120px]" data-testid="sm-confidence">
      <div className="h-2 flex-1 bg-muted rounded overflow-hidden">
        <div
          className="h-full bg-foreground"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs tabular-nums text-muted-foreground">
        {pct}%
      </span>
    </div>
  );
}

export default function StrategyMemory() {
  const { locale } = useLocale();
  const t = COPY[locale] || COPY.en;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("validated");
  const [dimension, setDimension] = useState("all");
  const [subject, setSubject] = useState("");
  const [selected, setSelected] = useState(null);
  const [kgOpen, setKgOpen] = useState(false);
  const [kgNodeId, setKgNodeId] = useState(null);
  // 11.5b — supersede modal + injection prefetch.
  const [supersedeOpen, setSupersedeOpen] = useState(false);
  const [injectionStats, setInjectionStats] = useState({});

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (status && status !== "all") params.set("status", status);
    if (dimension && dimension !== "all") params.set("dimension", dimension);
    if (subject) params.set("subject_kg_node_id", subject);
    params.set("limit", "100");
    api.get(`/strategy-memory/entries?${params.toString()}`)
      .then((r) => { if (alive) setItems(r.data?.items || []); })
      .catch(() => { if (alive) setItems([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [status, dimension, subject]);

  // 11.5b — batch-prefetch injection stats for validated entries in view.
  // Bailout when >50 rows to keep the burst reasonable; per-row hover
  // fetch would be a future enhancement.
  useEffect(() => {
    let alive = true;
    const targets = items.filter((it) => it.status === "validated");
    if (targets.length === 0 || targets.length > 50) return undefined;
    Promise.all(
      targets.map((it) =>
        api.get(`/strategy-memory/entries/${it.id}/injection-stats?window=7d`)
          .then((r) => [it.id, r.data])
          .catch(() => [it.id, null])),
    ).then((pairs) => {
      if (!alive) return;
      const map = {};
      pairs.forEach(([id, data]) => { if (data) map[id] = data; });
      setInjectionStats(map);
    });
    return () => { alive = false; };
  }, [items]);

  const rows = useMemo(() => items, [items]);

  return (
    <div className="p-6 space-y-5" data-testid="strategy-memory-page">
      <header className="flex items-start gap-3">
        <div className="size-10 rounded-md bg-foreground text-background grid place-items-center">
          <BrainCircuit className="size-5" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t.title}</h1>
          <p className="text-sm text-muted-foreground">{t.subtitle}</p>
        </div>
      </header>

      <Card data-testid="sm-filters">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {t.filters}
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">
              {t.status}
            </label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="sm-filter-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t.all}</SelectItem>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>{t.st[s]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">
              {t.dimension}
            </label>
            <Select value={dimension} onValueChange={setDimension}>
              <SelectTrigger data-testid="sm-filter-dimension">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t.all}</SelectItem>
                {DIMENSIONS.map((d) => (
                  <SelectItem key={d} value={d}>{t.dim[d]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">
              {t.subject}
            </label>
            <Input
              data-testid="sm-filter-subject"
              value={subject}
              placeholder={t.subject_ph}
              onChange={(e) => setSubject(e.target.value.trim())}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-sm text-muted-foreground" data-testid="sm-loading">
              {t.loading}
            </div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-sm text-muted-foreground text-center"
                 data-testid="sm-empty">
              {t.empty}
            </div>
          ) : (
            <div className="divide-y" data-testid="sm-list">
              {rows.map((it) => (
                <div
                  key={it.id}
                  data-testid={`sm-row-${it.id}`}
                  onClick={() => setSelected(it)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault(); setSelected(it);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  className="w-full text-left px-4 py-3 hover:bg-accent/60 transition-colors flex items-center gap-4 cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring/40"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="secondary" className="text-[10px]"
                             data-testid={`sm-dim-${it.dimension}`}>
                        {t.dim[it.dimension] || it.dimension}
                      </Badge>
                      <StatusBadge status={it.status} t={t} />
                      {/* Phase 18d — Hover the SM chip to preview full entry
                          (title, evidence, superseded_by, created_at, agent). */}
                      <span onClick={(e) => e.stopPropagation()}>
                        <StrategyMemoryBadge entryId={it.id} label={it.id.slice(0, 8)} />
                      </span>
                      {it.subject_kg_node_id && (
                        <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
                          <Network className="size-3" />
                          {it.subject_kg_node_id}
                        </span>
                      )}
                    </div>
                    <div className="text-sm leading-snug line-clamp-2">
                      {it.claim}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-1">
                      n={it.sample_size} · {it.created_by_agent}
                    </div>
                  </div>
                  <ConfidenceBar value={it.confidence} />
                  {(() => {
                    const s = injectionStats[it.id];
                    if (!s || (s.injection_count || 0) === 0) return null;
                    const last = s.last_injected_at
                      ? new Date(s.last_injected_at).toLocaleString(
                          locale === "de" ? "de-DE" : "en-US",
                          { dateStyle: "short", timeStyle: "short" })
                      : "—";
                    const label = locale === "de"
                      ? `🔁 ${s.injection_count}× letzte 7 Tage`
                      : `🔁 Injected ${s.injection_count}× last 7d`;
                    const tip = locale === "de"
                      ? `Letzte Verwendung: ${last}`
                      : `Last used: ${last}`;
                    return (
                      <span
                        data-testid={`sm-row-injection-badge-${it.id}`}
                        data-injection-count={s.injection_count}
                        title={tip}
                        className="ml-2 inline-flex items-center rounded-full border border-indigo-300 bg-indigo-50 text-indigo-900 px-2 py-0.5 text-[10px] font-medium"
                      >
                        {label}
                      </span>
                    );
                  })()}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Sheet open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <SheetContent
          side="right"
          className="sm:max-w-xl w-full overflow-y-auto"
          data-testid="sm-drawer"
        >
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  {t.drawer_title}
                  <StatusBadge status={selected.status} t={t} />
                </SheetTitle>
                <SheetDescription>{t.drawer_subtitle}</SheetDescription>
              </SheetHeader>

              <div className="mt-5 space-y-5">
                <div>
                  <div className="text-xs uppercase text-muted-foreground mb-1">
                    {t.dimension}
                  </div>
                  <Badge variant="secondary">
                    {t.dim[selected.dimension] || selected.dimension}
                  </Badge>
                </div>

                <div>
                  <div className="text-xs uppercase text-muted-foreground mb-1">
                    {t.claim}
                  </div>
                  <p className="text-sm leading-relaxed" data-testid="sm-drawer-claim">
                    {selected.claim}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs uppercase text-muted-foreground mb-1">
                      {t.confidence}
                    </div>
                    <ConfidenceBar value={selected.confidence} />
                  </div>
                  <div>
                    <div className="text-xs uppercase text-muted-foreground mb-1">
                      {t.sample_size}
                    </div>
                    <div className="text-sm tabular-nums" data-testid="sm-drawer-sample-size">
                      n = {selected.sample_size}
                    </div>
                  </div>
                </div>

                <div>
                  <div className="text-xs uppercase text-muted-foreground mb-1">
                    {t.claim_structured}
                  </div>
                  <pre
                    data-testid="sm-drawer-json"
                    className="text-xs bg-muted rounded p-3 overflow-x-auto whitespace-pre-wrap break-words"
                  >
{JSON.stringify(selected.claim_structured || {}, null, 2)}
                  </pre>
                </div>

                <div>
                  <div className="text-xs uppercase text-muted-foreground mb-1">
                    {t.created_by_agent}
                  </div>
                  <div className="text-sm font-mono">
                    {selected.created_by_agent || "—"}
                  </div>
                </div>

                <div>
                  <div className="text-xs uppercase text-muted-foreground mb-1">
                    {t.evidence}
                  </div>
                  {(selected.evidence_agent_run_ids || []).length === 0 ? (
                    <div className="text-xs text-muted-foreground">
                      {t.no_evidence}
                    </div>
                  ) : (
                    <ul className="text-xs font-mono space-y-1"
                        data-testid="sm-drawer-evidence">
                      {selected.evidence_agent_run_ids.map((rid) => (
                        <li key={rid} className="flex items-center gap-2">
                          <ExternalLink className="size-3 text-muted-foreground" />
                          {rid}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div>
                  <div className="text-xs uppercase text-muted-foreground mb-1">
                    {t.evidence_kg_paths}
                  </div>
                  {(selected.evidence_kg_paths || []).length === 0 ? (
                    <div className="text-xs text-muted-foreground">
                      {t.no_kg_paths}
                    </div>
                  ) : (
                    <EvidenceKgPaths
                      paths={selected.evidence_kg_paths}
                      locale={locale}
                      onOpenNode={(nid) => {
                        setKgNodeId(nid);
                        setKgOpen(true);
                      }}
                    />
                  )}
                </div>

                {selected.superseded_by && (
                  <div className="text-xs text-muted-foreground">
                    {t.superseded_by}:{" "}
                    <span className="font-mono">{selected.superseded_by}</span>
                  </div>
                )}

                {/* Phase 13b — Autopilot actions citing this entry. */}
                <AutopilotUsageSection
                  entryId={selected.id}
                  locale={locale}
                />

                <div className="pt-2 flex flex-wrap gap-2">
                  {selected.subject_kg_node_id ? (
                    <Button
                      data-testid="sm-drawer-kg-btn"
                      onClick={() => {
                        setKgNodeId(selected.subject_kg_node_id);
                        setKgOpen(true);
                      }}
                    >
                      <Network className="size-4 mr-2" />
                      {t.kg_button}
                    </Button>
                  ) : (
                    <Button variant="outline" disabled
                            data-testid="sm-drawer-kg-btn-disabled">
                      {t.kg_no_node}
                    </Button>
                  )}

                  {/* 10.3b: curator actions — RBAC-gated. */}
                  {auth.hasPermission("strategy_memory.write") && (
                    <>
                      {selected.status === "validated" && (
                        <Button
                          variant="outline"
                          data-testid="sm-drawer-supersede-btn"
                          onClick={() => setSupersedeOpen(true)}
                        >
                          <Replace className="size-4 mr-2" />
                          {locale === "de" ? "Ersetzen" : "Supersede"}
                        </Button>
                      )}
                      {selected.status !== "archived" && (
                        <Button
                          variant="outline"
                          data-testid="sm-drawer-archive-btn"
                          onClick={async () => {
                            try {
                              await api.post(
                                `/strategy-memory/entries/${selected.id}/archive`);
                              toast.success(locale === "de"
                                ? "Eintrag archiviert."
                                : "Entry archived.");
                              setSelected(null);
                              // Trigger a re-fetch by nudging a filter.
                              setStatus((s) => s);
                              const params = new URLSearchParams();
                              if (status && status !== "all")
                                params.set("status", status);
                              if (dimension && dimension !== "all")
                                params.set("dimension", dimension);
                              if (subject)
                                params.set("subject_kg_node_id", subject);
                              params.set("limit", "100");
                              const r = await api.get(
                                `/strategy-memory/entries?${params.toString()}`);
                              setItems(r.data?.items || []);
                            } catch (e) {
                              toast.error("Archive failed.");
                            }
                          }}
                        >
                          {locale === "de" ? "Archivieren" : "Archive"}
                        </Button>
                      )}
                      {selected.status === "archived" && (
                        <Button
                          variant="outline"
                          data-testid="sm-drawer-restore-btn"
                          onClick={async () => {
                            try {
                              await api.post(
                                `/strategy-memory/entries/${selected.id}/restore`);
                              toast.success(locale === "de"
                                ? "Eintrag wiederhergestellt."
                                : "Entry restored.");
                              setSelected(null);
                              const params = new URLSearchParams();
                              if (status && status !== "all")
                                params.set("status", status);
                              params.set("limit", "100");
                              const r = await api.get(
                                `/strategy-memory/entries?${params.toString()}`);
                              setItems(r.data?.items || []);
                            } catch (e) {
                              toast.error("Restore failed.");
                            }
                          }}
                        >
                          {locale === "de" ? "Wiederherstellen" : "Restore"}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <KnowledgeGraphDrawer
        nodeId={kgNodeId}
        open={kgOpen}
        onClose={() => setKgOpen(false)}
      />

      <SupersedeModal
        entry={selected}
        open={supersedeOpen}
        onOpenChange={setSupersedeOpen}
        locale={locale}
        onSuperseded={async () => {
          // Refetch list so the old entry now shows superseded status.
          const params = new URLSearchParams();
          if (status && status !== "all") params.set("status", status);
          if (dimension && dimension !== "all") params.set("dimension", dimension);
          if (subject) params.set("subject_kg_node_id", subject);
          params.set("limit", "100");
          try {
            const r = await api.get(
              `/strategy-memory/entries?${params.toString()}`);
            setItems(r.data?.items || []);
          } catch (_e) { /* noop */ }
          setSelected(null);
        }}
      />
    </div>
  );
}
